/**
 * Reports (docs/REPORTS.md): comments of kind `report`, each with its report posted with it, a label naming its series,
 * the period it covers and the items it's based on. A series is shown as one card, its newest, the earlier ones under
 * it; a report without a label is a series of its own.
 */

import type { CommentOut, Period, SpaceOut } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { commentAnchor, conversationPath } from "./conversation.ts";
import { formatDate, formatInstant } from "./format.ts";
import { spaceUrl } from "./paths.ts";

/** A report's frame in a conversation: the accent edge that sets it apart from the talk around it. */
export const REPORT_EDGE = "border-l-4 border-l-blue-600";

/** Whether `c` is a report. */
export const isReport = (c: Pick<CommentOut, "kind">): boolean => c.kind === "report";

/** The report posted with `c` (its attachment made for it), if it has one. */
const reportOf = (c: Pick<CommentOut, "attachments">) => c.attachments?.find((a) => a.posted);

/** A report's title: its posted item's. */
export const reportTitle = (c: CommentOut): string => reportOf(c)?.title || t("common.untitled");

/** One end of a period, as a date: a date as it's written, an instant as its day here. */
const dayOf = (s: string): string => (s.length === 10 ? formatDate(s) : formatInstant(s, { dateStyle: "medium" }));

/** A period in words: its day, or from one day to another. */
export function periodText(p: Period): string {
  const [from, to] = [dayOf(p.from), dayOf(p.to)];
  return from === to ? from : t("reports.period", { from, to });
}

/** Where a report is, in its conversation (a link to the comment in its place). */
export const placeHref = (space: Pick<SpaceOut, "id">, c: CommentOut): string =>
  `${spaceUrl(space, conversationPath(c))}#${commentAnchor(c.id)}`;

/** A series: its newest (`latest`), and the earlier ones, newest first. */
export interface Series<T> {
  latest: T;
  earlier: T[];
}

/** A labelled report's series among others of one conversation: its label. */
const byLabel = (c: CommentOut): string | null => c.label ?? null;

/** A labelled report's series among others of many conversations: its conversation and label. */
export const byPlace = (c: CommentOut): string | null => (c.label ? `${conversationPath(c)}|${c.label}` : null);

/**
 * `list` (each with its first comment, `rootOf`) with each labelled series of reports (by `seriesKey`: in one
 * conversation, a label) gathered under its newest, which takes the place of the series; the rest as they were, each
 * a series of its own with nothing earlier.
 */
export function seriesOf<T>(
  list: readonly T[],
  rootOf: (x: T) => CommentOut,
  seriesKey: (c: CommentOut) => string | null = byLabel,
): Series<T>[] {
  const keyOf = (x: T) => {
    const c = rootOf(x);
    return isReport(c) ? seriesKey(c) : null;
  };
  const all = new Map<string, T[]>();
  for (const x of list) {
    const k = keyOf(x);
    if (k !== null) {
      all.set(k, [...(all.get(k) ?? []), x]);
    }
  }
  const newest = new Map<string, T[]>(
    [...all].map(([k, xs]) => [k, [...xs].sort((a, b) => (rootOf(a).id < rootOf(b).id ? 1 : -1))]),
  );
  const out: Series<T>[] = [];
  for (const x of list) {
    const k = keyOf(x);
    const series = k === null ? undefined : newest.get(k);
    if (!series) {
      out.push({ latest: x, earlier: [] });
    } else if (series[0] === x) {
      out.push({ latest: x, earlier: series.slice(1) });
    }
  }
  return out;
}
